import { useState } from 'react'
import { RotateCcw } from 'lucide-react'

import { pluralize } from '@/lib/pluralize'
import { Button } from '@/ui/button'
import { Slider } from '@/ui/slider'

import { useGridContext } from './data-grid-context'
import {
  DataGridFilterBody,
  DataGridFilterHeader,
  DataGridFilterPanel,
} from './data-grid-filter-panel'
import { DataGridFilterChip, type DataGridFilterPresentationProps } from './data-grid-filter-shared'

const MIN_HOURS = 1
const MAX_HOURS = 24 * 365
const PRESETS = [
  { hours: 1, label: 'Past hour' },
  { hours: 24, label: 'Past 24 hours' },
  { hours: 24 * 7, label: 'Past 7 days' },
  { hours: 24 * 30, label: 'Past 30 days' },
] as const
const SLIDER_STEPS = 1000
const DEFAULT_HOURS = 24 * 30
const HOURS_PER_DAY = 24
const HOURS_PER_WEEK = HOURS_PER_DAY * 7
const HOURS_PER_AVERAGE_MONTH = HOURS_PER_DAY * (365 / 12)
const cutoffFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function hoursToSliderValue(hours: number): number {
  const progress = Math.log(hours / MIN_HOURS) / Math.log(MAX_HOURS / MIN_HOURS)
  return Math.round(progress * SLIDER_STEPS)
}

function sliderValueToHours(value: number): number {
  const preset = PRESETS.find((item) => hoursToSliderValue(item.hours) === value)
  if (preset) return preset.hours
  const progress = value / SLIDER_STEPS
  return Math.max(
    MIN_HOURS,
    Math.min(
      MAX_HOURS,
      Math.round(MIN_HOURS * (MAX_HOURS / MIN_HOURS) ** progress),
    ),
  )
}

export function formatRecentWindow(hours: number): string {
  const preset = PRESETS.find((item) => item.hours === hours)
  if (preset) return preset.label
  if (hours === MAX_HOURS) return 'Past year'

  if (hours < HOURS_PER_DAY * 2) {
    return `Past ${pluralize(hours, 'hour')}`
  }
  if (hours < HOURS_PER_DAY * 14) {
    const days = Math.round(hours / HOURS_PER_DAY)
    return `Past ${pluralize(days, 'day')}`
  }
  if (hours < HOURS_PER_DAY * 60) {
    const weeks = Math.round(hours / HOURS_PER_WEEK)
    return `Past ${pluralize(weeks, 'week')}`
  }

  const months = Math.round(hours / HOURS_PER_AVERAGE_MONTH)
  return months >= 12 ? 'Past year' : `Past ${months} months`
}

export function formatRecentCutoff(hours: number, now: Date = new Date()): string {
  const cutoff = new Date(now.getTime() - hours * 60 * 60 * 1000)
  return `Since ${cutoffFormatter.format(cutoff)}`
}

export function DataGridTimestampFilter({
  column,
  presentation,
  onBack,
}: DataGridFilterPresentationProps) {
  const { grid } = useGridContext()
  const current = grid.criteria.filter?.find((filter) => filter.column === column.id)
  const relative = current?.operator === 'relative'
    && typeof current.values === 'object'
    && !Array.isArray(current.values)
    && 'amount' in current.values
    ? current.values
    : null
  const hours = relative
    ? relative.amount * (relative.unit === 'days' ? HOURS_PER_DAY : 1)
    : null
  const [open, setOpen] = useState(false)
  const [draftHours, setDraftHours] = useState(hours ?? DEFAULT_HOURS)

  function changeOpen(next: boolean) {
    if (next) setDraftHours(hours ?? DEFAULT_HOURS)
    setOpen(next)
  }

  function changeValue(next: number | null) {
    grid.setFilters([
      ...(grid.criteria.filter ?? []).filter((filter) => filter.column !== column.id),
      ...(next === null ? [] : [{
        column: column.id,
        operator: 'relative',
        values: next % HOURS_PER_DAY === 0
          ? { amount: next / HOURS_PER_DAY, unit: 'days' as const }
          : { amount: next, unit: 'hours' as const },
      }]),
    ])
  }

  const summary = current && !relative ? 'Filtered' : hours === null
    ? undefined
    : formatRecentWindow(hours)
  const panel = (
    <DataGridFilterPanel presentation={presentation} className="w-80">
      <DataGridFilterHeader
        label={column.label}
        description="Show records observed within a recent time window."
        onBack={onBack}
      />
      <DataGridFilterBody className="gap-3">
        <div className="grid grid-cols-2 gap-1.5" aria-label="Common time windows">
          {PRESETS.map((preset) => (
            <Button
              key={preset.hours}
              type="button"
              size="sm"
              variant={hours === preset.hours ? 'secondary' : 'outline'}
              onClick={() => {
                setDraftHours(preset.hours)
                changeValue(preset.hours)
              }}
            >
              {preset.label}
            </Button>
          ))}
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-start justify-between gap-3">
            <span className="font-medium">Custom window</span>
            <span className="text-right tabular-nums">
              <span className="block">{formatRecentWindow(draftHours)}</span>
              <span className="text-muted-foreground block text-[11px]">
                {formatRecentCutoff(draftHours)}
              </span>
            </span>
          </div>
          <Slider
            label={`${column.label} window`}
            min={0}
            max={SLIDER_STEPS}
            value={hoursToSliderValue(draftHours)}
            onValueChange={(next) => setDraftHours(sliderValueToHours(next))}
            onValueCommitted={(next) => changeValue(sliderValueToHours(next))}
            formatValue={(next) => formatRecentWindow(sliderValueToHours(next))}
          />
          <div className="text-muted-foreground flex justify-between text-[11px]">
            <span>1 hour</span>
            <span>1 year</span>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          disabled={!current}
          onClick={() => {
            setDraftHours(DEFAULT_HOURS)
            changeValue(null)
          }}
        >
          <RotateCcw aria-hidden className="size-3.5" />
          Clear filter
        </Button>
      </DataGridFilterBody>
    </DataGridFilterPanel>
  )

  if (presentation !== 'chip') return panel

  return (
    <DataGridFilterChip column={column} summary={summary} open={open} onOpenChange={changeOpen}>
      {panel}
    </DataGridFilterChip>
  )
}
